import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from '@fullcalendar/timegrid';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import { useState, useRef, useEffect } from 'react';
import { useDispatch, useLocation, useSelector } from 'src/redux/store';
import { getEvents, selectEvent, selectRange } from 'src/redux/slices/calendar';
import useResponsive from 'src/hooks/useResponsive';
import { CalendarStyle, CalendarToolbar } from 'src/sections/@dashboard/calendar';
import { convertStringArrayToNumber } from 'src/utils/functions';
import useAuth from 'src/hooks/useAuth';
import { useNavigate } from 'react-router';
import { format, parseISO } from 'date-fns';
import Scrollbar from 'src/components/Scrollbar';
import CalendarSkeleton from 'src/components/skeleton/calendario/CalendarioSkeleton';
import AddEventDialog from 'src/sections/@dashboard/calendar/CalendarForm';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from 'src/@/components/ui/select';
import v2_actionCaledarioDetalhes from 'src/actions/v2/calendario/v2_actionCaledarioDetalhes';
import v2_actionCalendarios from 'src/actions/v2/calendario/v2_actionCalendarios';
import v2_actionCalendarioEditarEvento from 'src/actions/v2/calendario/v2_actionCalendarioEditarEvento';
import v2_actionCalendarioCriarEvento from 'src/actions/v2/calendario/v2_actionCalendarioCriarEvento';
import EmptyContentGhost from 'src/components/EmptyContentGhost';
import v2_actionCalendarioEvento from 'src/actions/v2/calendario/v2_actionCalendarioEvento';
import { toast } from 'src/@/components/ui/use-toast';
import Iconify from 'src/components/Iconify';
import { CALENDARIO_DESIGN as D } from './Calendario.design';

// ----------------------------------------------------------------------

const EventContent = ({ info, isDesktop, events }) => {
    const event = events.find((e) => e.id === info.event.id);
    const isLais = event?.source_event === "lais";
    const customColor = event?._color || event?.color;

    const timeLabel = event?.allDay ? 'Dia todo' : (
        new Date(info.event.start).toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
        })
    );

    const titleText = info?.event?.title.substr(0, (isDesktop ? 15 : 5))
        + (info?.event?.title?.length > (isDesktop ? 15 : 5) ? '...' : '');

    if (customColor) {
        return (
            <div
                className="relative z-10 flex flex-row items-center gap-1.5 rounded-md p-1 h-full"
                style={{ backgroundColor: `${customColor}22` }}
            >
                <div
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: customColor }}
                />
                <span className="text-[10px] font-semibold text-gray-600 whitespace-nowrap">
                    {timeLabel}
                </span>
                <span className="text-[10px] font-medium text-gray-700 truncate">
                    {titleText}
                </span>
            </div>
        );
    }

    return (
        <div className={`relative z-10 ${isLais ? D.eventContent.lais : D.eventContent.external}`}>
            <div className={`${D.eventContent.timeText} ${isLais ? D.eventContent.laisText : D.eventContent.externalText}`}>
                <span>{timeLabel}</span>
            </div>
            <div className={`${D.eventContent.titleText} ${isLais ? D.eventContent.laisText : D.eventContent.externalText}`}>
                {titleText}
            </div>
        </div>
    );
};

const groupEventsByDate = (events) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!Array.isArray(events) || events.length === 0) {
        return {};
    }

    return events.reduce((groups, event) => {
        try {
            if (!event?.start) return groups;

            const eventDate = new Date(event.start);

            if (isNaN(eventDate.getTime())) {
                console.warn('Data inválida para evento:', event);
                return groups;
            }

            const dateOnly = new Date(
                eventDate.getFullYear(),
                eventDate.getMonth(),
                eventDate.getDate()
            );

            if (dateOnly < today) {
                return groups;
            }

            const dateKey = format(eventDate, 'yyyy-MM-dd');

            if (!groups[dateKey]) {
                groups[dateKey] = [];
            }

            groups[dateKey].push({
                ...event,
                localStart: eventDate,
                displayTime: event.time_start
            });

            groups[dateKey].sort((a, b) => {
                const timeA = a.time_start.split(':').map(Number);
                const timeB = b.time_start.split(':').map(Number);
                return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
            });

            return groups;
        } catch (err) {
            console.warn('Erro ao processar evento:', err);
            return groups;
        }
    }, {});
};

export default function Calendario() {
    const { user } = useAuth();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isDesktop = useResponsive('up', 'lg');
    const calendarRef = useRef(null);
    const [date, setDate] = useState(new Date());
    const [view, setView] = useState('dayGridMonth');
    const { events, selectedEventId, isLoading } = useSelector((state) => state.calendar);
    const selectedEvent = useSelector((state) => state.calendar.events.find((event) => event.id === selectedEventId));
    const [isEdit, setIsEdit] = useState(false);
    const [selectedCategories, setSelectedCategories] = useState(['lais']);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [config, setConfig] = useState(null);

    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const calenderId = params.get('id');
    const [listCalenders, setListCalenders] = useState([]);
    const [selectCalenderId, setSelectCalenderId] = useState(calenderId);
    const [loadingCalendarios, setLoadingCalendarios] = useState(false);

    const groupedEvents = groupEventsByDate(events);

    const numberArray = convertStringArrayToNumber(config?.daysHidden || []);

    const [event, setEvent] = useState({
        id: '',
        title: '',
        description: '',
        startDate: new Date(),
        endDate: new Date(),
        allDay: false,
        source_event: 'lais',
        link: '',
        event_type: 'simple_event',
        time_start: '',
        time_end: '',
        color: null
    });

    function handleChangeValue(event) {
        const { name, value } = event.target;
        setEvent((prev) => ({
            ...prev,
            [name]: value,
        }));
    }

    function handleGetEvents(blockLoading, calendarId) {
        let querys = `?calendar=1&calendar_id=${calendarId ? calendarId : selectCalenderId}`;
        dispatch(getEvents(user.clienteId, querys, blockLoading));
    }

    async function buscarConfiguracoes(id) {
        try {
            const response = await v2_actionCaledarioDetalhes(id);
            setConfig(response.data.data.config);
        } catch (error) {
            console.log('error', error);
        }
    }

    async function getListCalenders() {
        try {
            setLoadingCalendarios(true);
            const response = await v2_actionCalendarios(user.clienteId);
            if (response.status === 200) {
                setListCalenders(response.data.data);
                setLoadingCalendarios(false);

                if (response.data.data.length > 0) {
                    const targetId = calenderId || response.data.data[0]._id;

                    if (targetId !== selectCalenderId || !selectCalenderId) {
                        handleGetEvents(false, targetId);
                        setSelectCalenderId(targetId);
                        buscarConfiguracoes(targetId);
                    }

                    if (!calenderId) {
                        navigate({
                            pathname: '/dashboard/calendario',
                            search: '?id=' + targetId
                        }, { replace: true });
                    }
                }
            }
        } catch (error) {
            setLoadingCalendarios(false);
            toast({
                title: 'Algo deu errado',
                description: 'Não foi possível buscar os calendários',
                variant: 'destructive'
            });
            console.log(error);
        }
    }

    useEffect(() => {
        if (listCalenders.length > 0 && calenderId) {
            if (calenderId !== selectCalenderId) {
                setSelectCalenderId(calenderId);
                handleGetEvents(false, calenderId);
                buscarConfiguracoes(calenderId);
            }
        } else {
            getListCalenders();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calenderId, listCalenders.length]);

    useEffect(() => {
        const calendarEl = calendarRef.current;
        if (calendarEl) {
            const calendarApi = calendarEl.getApi();
            const newView = isDesktop ? 'dayGridMonth' : 'timeGridWeek';
            calendarApi.changeView(newView);
            setView(newView);
        }
    }, [isDesktop]);

    const handleClickToday = () => {
        const calendarEl = calendarRef.current;
        if (calendarEl) {
            const calendarApi = calendarEl.getApi();
            calendarApi.today();
            setDate(calendarApi.getDate());
        }
    };

    const handleChangeView = (newView) => {
        const calendarEl = calendarRef.current;
        if (calendarEl) {
            const calendarApi = calendarEl.getApi();
            calendarApi.changeView(newView);
            setView(newView);
        }
    };

    const handleClickDatePrev = () => {
        const calendarEl = calendarRef.current;
        if (calendarEl) {
            const calendarApi = calendarEl.getApi();
            calendarApi.prev();
            setDate(calendarApi.getDate());
        }
    };

    const handleClickDateNext = () => {
        const calendarEl = calendarRef.current;
        if (calendarEl) {
            const calendarApi = calendarEl.getApi();
            calendarApi.next();
            setDate(calendarApi.getDate());
        }
    };

    const handleSelectEvent = async (arg) => {
        try {
            const response = await v2_actionCalendarioEvento(arg.event.id);
            let eventData = response.data.data;

            if (response.status === 200) {
                dispatch(selectEvent(arg.event?.id));

                const start = new Date(arg.event.start || eventData.start?.dateTime || eventData.start);
                const end = new Date(arg.event.end || eventData.end?.dateTime || eventData.end);

                setEvent({
                    id: arg.event?.id,
                    title: arg.event?.title,
                    description: eventData?.description,
                    time_start: format(start, 'HH:mm'),
                    time_end: format(end, 'HH:mm'),
                    allDay: arg.event?.allDay,
                    source_event: arg.event?.source_event,
                    link: eventData.link,
                    event_type: eventData?.event_type,
                    startDate: start,
                    endDate: end,
                    start: start,
                    end: end,
                    color: eventData?.color || null
                });
                setIsEdit(true);
                setDialogOpen(true);
            }
        } catch (error) {
            console.error('Erro ao selecionar evento:', error);
        }
    };

    const handleDropEvent = async ({ event }) => {
        try {
            let scope = {
                id: event.id,
                "start.dateTime": event.start,
                "end.dateTime": event.end,
                time_start: format(new Date(event.start), 'HH:mm'),
                time_end: format(new Date(event.end), 'HH:mm'),
                update_in: new Date()
            };
            handleEdit(scope, true);
        } catch (error) {
            console.error(error);
        }
    };

    const handleEditModal = async () => {
        try {
            const combineDateAndTime = (date, timeStr) => {
                const [hours, minutes] = timeStr.split(':').map(Number);
                const newDate = new Date(date);
                newDate.setHours(hours, minutes, 0);
                return newDate;
            };

            let baseStartDate = event.startDate || event.start;
            let baseEndDate = event.endDate || event.end;

            if (typeof baseStartDate === 'string') baseStartDate = new Date(baseStartDate);
            if (typeof baseEndDate === 'string') baseEndDate = new Date(baseEndDate);

            const startDate = combineDateAndTime(baseStartDate, event.time_start);
            const endDate = combineDateAndTime(baseEndDate, event.time_end);

            if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                throw new Error('Datas ou horários inválidos');
            }

            let scope = {
                id: event.id,
                "start.dateTime": startDate.toISOString(),
                "end.dateTime": endDate.toISOString(),
                time_start: event.time_start,
                time_end: event.time_end,
                update_in: new Date().toISOString(),
                summary: event.title,
                description: event.description,
                event_type: event.event_type,
                allDay: event.allDay,
                link: event.link,
                color: event.color || null
            };

            await handleEdit(scope);
        } catch (error) {
            console.error('Erro ao editar evento:', error);
        }
    };

    function handleClose() {
        setEvent({
            id: '',
            title: '',
            description: '',
            startDate: new Date(),
            endDate: new Date(),
            allDay: false,
            source_event: 'lais',
            link: '',
            event_type: 'simple_event',
            color: null
        });
        setDialogOpen(!dialogOpen);
        setIsEdit(false);
    }

    async function handleCreate() {
        if (!calenderId) {
            toast({
                title: 'Calendário não selecionado',
                description: 'Selecione um calendário para criar o evento',
                variant: 'destructive'
            });
            return;
        }

        let dataCreate = {
            "event_type": event.event_type,
            "description": event.description,
            "summary": event.title,
            "event_id": "",
            "status": "",
            "creator_email": "",
            "location": "",
            "is_meeting": false,
            "user_id": user.id,
            "attendees": [],
            "meeting_link": "",
            "cliente_id": user.clienteId,
            "startDate": format(event.startDate, "yyyy-MM-dd"),
            "endDate": format(event.endDate, "yyyy-MM-dd"),
            "time_start": event.time_start,
            "time_end": event.time_end,
            "source_event": event.source_event,
            "calendar_id": calenderId,
            "color": event.color || null
        };

        try {
            await v2_actionCalendarioCriarEvento(user.clienteId, { scope: dataCreate });
            handleGetEvents(true, calenderId);
            setEvent({
                id: '',
                title: '',
                description: '',
                startDate: new Date(),
                endDate: new Date(),
                allDay: false,
                source_event: 'lais',
                link: '',
                event_type: 'simple_event',
                time_start: '',
                time_end: '',
                calendar_id: calenderId,
                color: null
            });
        } catch (error) {
            console.log(error);
        }
    }

    const handleEdit = async (scopeData, blockLoading) => {
        try {
            await v2_actionCalendarioEditarEvento(scopeData.id, { scope: scopeData });
            handleGetEvents(blockLoading, calenderId);
        } catch (error) {
            console.log(error);
        }
    };

    const handleSidebarEventClick = (evt) => {
        const getSafeDate = (dateInfo) => {
            if (!dateInfo) return new Date();
            if (dateInfo instanceof Date) return !isNaN(dateInfo) ? dateInfo : new Date();
            const parsed = new Date(dateInfo);
            return !isNaN(parsed) ? parsed : new Date();
        };

        const startDate = getSafeDate(evt?.localStart?.getTime ? evt.localStart : evt?.start);
        const endDate = evt?.end ? getSafeDate(evt.end) : startDate;

        setIsEdit(true);
        setEvent({
            id: evt?.id,
            title: evt?.title || evt?.summary || '',
            description: evt?.description || '',
            startDate,
            endDate,
            allDay: Boolean(evt?.allDay),
            source_event: evt?.source_event || 'lais',
            link: evt?.link || '',
            event_type: evt?.event_type || 'simple_event',
            time_start: evt?.displayTime || format(startDate, 'HH:mm'),
            time_end: format(endDate, 'HH:mm'),
            color: evt?.color || null,
        });
        setDialogOpen(true);
    };

    // Loading state
    if (loadingCalendarios) {
        return <CalendarSkeleton />;
    }

    // Empty state
    if (listCalenders.length === 0) {
        return (
            <div className={D.emptyState.container}>
                <EmptyContentGhost
                    title="Nenhum calendário encontrado"
                    description="Crie um calendário para começar a adicionar eventos."
                    icon="mdi:calendar-plus"
                    button={{
                        admin: true,
                        user: user,
                        label: "Criar calendário",
                        onClick: () => navigate('/dashboard/configuracoes/calendarios')
                    }}
                />
            </div>
        );
    }

    return (
        <div className={D.page.container}>
            {/* Sticky Header */}
            <div className={D.header.container}>
                <div className={D.header.row}>
                    {/* Left: Icon + Title + Calendar Select */}
                    <div className={D.header.left}>
                        <div className={D.header.iconBox}>
                            <Iconify icon="eva:calendar-outline" width={20} className="text-violet-600" />
                        </div>
                        <span className={D.header.title}>Calendário</span>
                        <Select
                            value={selectCalenderId}
                            onValueChange={(e) => {
                                navigate({
                                    pathname: '/dashboard/calendario',
                                    search: '?id=' + e
                                });
                                setSelectCalenderId(e);
                                handleGetEvents(true, e);
                                buscarConfiguracoes(e);
                            }}
                        >
                            <SelectTrigger className={D.header.calendarSelect.trigger}>
                                <SelectValue placeholder="Selecione um calendário..." />
                                <SelectContent className={D.header.calendarSelect.content}>
                                    {listCalenders.map((option) => (
                                        <SelectItem
                                            key={option._id}
                                            value={option._id}
                                            className={D.header.calendarSelect.item}
                                        >
                                            {option.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </SelectTrigger>
                        </Select>
                    </div>

                    {/* Center + Right via CalendarToolbar */}
                    <CalendarToolbar
                        date={date}
                        view={view}
                        onNextDate={handleClickDateNext}
                        onPrevDate={handleClickDatePrev}
                        onToday={handleClickToday}
                        onChangeView={handleChangeView}
                        onOpen={() => setDialogOpen(true)}
                    />
                </div>
            </div>

            {/* Content: Calendar + Sidebar */}
            <div className={D.content.container}>
                {/* Calendar Area */}
                <div className={D.content.calendarArea}>
                    <div className={D.content.calendarWrapper}>
                        <CalendarStyle>
                            <FullCalendar
                                hiddenDays={numberArray}
                                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                                weekends
                                slotMinTime={config?.startTime || '00:00:00'}
                                slotMaxTime={config?.endTime || '23:59:59'}
                                slotDuration={config?.slotDuration || '00:30:00'}
                                editable
                                droppable
                                selectable
                                eventOverlap
                                allDaySlot
                                events={events.map(({ color, ...evt }) => ({ ...evt, _color: color }))}
                                firstDay={0}
                                ref={calendarRef}
                                rerenderDelay={10}
                                initialDate={date}
                                initialView={view}
                                dayMaxEventRows={1}
                                dayMaxEvents={true}
                                eventDisplay="block"
                                headerToolbar={false}
                                allDayMaintainDuration
                                eventResizableFromStart
                                select={(value) => {
                                    setEvent({
                                        id: '',
                                        title: '',
                                        description: '',
                                        startDate: value.start,
                                        endDate: value.end,
                                        allDay: value.allDay,
                                        time_start: format(new Date(value.start), 'HH:mm'),
                                        time_end: format(new Date(value.end), 'HH:mm'),
                                        source_event: 'lais',
                                        link: '',
                                        event_type: 'simple_event',
                                        color: null
                                    });
                                    setDialogOpen(true);
                                }}
                                eventDrop={handleDropEvent}
                                eventClick={handleSelectEvent}
                                eventResize={handleDropEvent}
                                eventContent={(info) => (
                                    <EventContent info={info} isDesktop={isDesktop} events={events} />
                                )}
                                height="calc(100vh - 130px)"
                                locale={ptBrLocale}
                            />
                        </CalendarStyle>
                    </div>
                </div>

                {/* Right Sidebar */}
                {isDesktop && (
                    <div className={D.sidebar.container}>
                        <div className={D.sidebar.header}>
                            <h2 className={D.sidebar.headerTitle}>Próximos Eventos</h2>
                        </div>
                        <div className={D.sidebar.body}>
                            <Scrollbar className="h-full">
                                <div className="space-y-6 px-2 pb-4">
                                    {Object.keys(groupedEvents).length === 0 && (
                                        <div className="flex flex-col items-center justify-center py-12 text-center">
                                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-100 to-violet-50 flex items-center justify-center mb-3 shadow-sm shadow-violet-200/50">
                                                <Iconify icon="eva:calendar-outline" width={24} className="text-violet-400" />
                                            </div>
                                            <p className="text-sm text-violet-400/80 font-medium">Nenhum evento próximo</p>
                                        </div>
                                    )}
                                    {Object.keys(groupedEvents)
                                        .sort((a, b) => new Date(a) - new Date(b))
                                        .map((dateKey) => (
                                            <div key={dateKey} className={D.sidebar.dateGroup}>
                                                <div className="flex items-center gap-2 mt-2">
                                                    <div className={D.sidebar.dateDot} />
                                                    <span className={D.sidebar.dateLabel}>
                                                        {format(parseISO(dateKey), 'dd/MM')}
                                                    </span>
                                                    <div className={D.sidebar.dateLine} />
                                                </div>
                                                <div className="flex flex-col gap-2">
                                                    {groupedEvents[dateKey].map((evt) => (
                                                        <div
                                                            key={evt.id}
                                                            onClick={() => handleSidebarEventClick(evt)}
                                                            className={D.eventCard.container}
                                                        >
                                                            <div className="flex flex-row items-center gap-3 w-full">
                                                                {evt.color && (
                                                                    <div
                                                                        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                                                        style={{ backgroundColor: evt.color }}
                                                                    />
                                                                )}
                                                                <span className={D.eventCard.time}>
                                                                    {evt.displayTime}
                                                                </span>
                                                                <div className={D.eventCard.divider} />
                                                                <span className={D.eventCard.title}>
                                                                    {evt.title || evt.summary}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </Scrollbar>
                        </div>
                    </div>
                )}
            </div>

            {/* Dialog at root level */}
            <AddEventDialog
                user={user}
                open={dialogOpen}
                onOpenChange={() => {
                    setDialogOpen(!dialogOpen);
                    setIsEdit(false);
                }}
                onClose={handleClose}
                event={event}
                onEdit={handleEditModal}
                onCreate={handleCreate}
                changeValue={handleChangeValue}
                onCallback={handleGetEvents}
                isEdit={isEdit}
            />
        </div>
    );
}
